import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FiPhone, FiMail, FiMapPin, FiClock, FiSend } from 'react-icons/fi';
import SEO from '../components/SEO';

const zoomIn = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1.1);
  }
`;

const ContactContainer = styled.div`
  padding-top: 0;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, rgba(26, 77, 128, 0.8) 0%, rgba(44, 62, 80, 0.7) 100%), url('/IMG-20250802-WA0055.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  color: ${props => props.theme.colors.white};
  padding: 120px 0 80px;
  text-align: center;
  position: relative;
  min-height: 60vh;
  display: flex;
  align-items: center;
  animation: ${zoomIn} 10s ease-in-out infinite;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    background-attachment: scroll;
    min-height: 50vh;
    padding: 100px 0 60px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Section = styled.section`
  padding: 80px 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: 60px;
  color: ${props => props.theme.colors.primary};
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.secondary};
    margin: 20px auto 0;
    border-radius: 2px;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactForm = styled(motion.form)`
  background: ${props => props.theme.colors.white};
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 15px 35px rgba(0,0,0,0.1);
  
  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 30px;
    font-size: 1.5rem;
  }
  
  .form-group {
    margin-bottom: 20px;
    
    label {
      display: block;
      margin-bottom: 8px;
      color: ${props => props.theme.colors.dark};
      font-weight: 500;
    }
    
    input, textarea, select {
      width: 100%;
      padding: 12px 15px;
      border: 2px solid ${props => props.theme.colors.light};
      border-radius: 8px;
      font-size: 1rem;
      transition: border-color 0.3s ease;
      
      &:focus {
        outline: none;
        border-color: ${props => props.theme.colors.secondary};
      }
    }
    
    textarea {
      resize: vertical;
      min-height: 120px;
    }
  }
  
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      grid-template-columns: 1fr;
    }
  }
  
  .submit-btn {
    background: ${props => props.theme.colors.primary};
    color: white;
    padding: 15px 30px;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 10px;
    
    &:hover {
      background: ${props => props.theme.colors.dark};
      transform: translateY(-2px);
    }
  }
`;

const ContactInfo = styled(motion.div)`
  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 30px;
    font-size: 1.5rem;
  }
  
  .contact-item {
    display: flex;
    align-items: flex-start;
    gap: 15px;
    margin-bottom: 30px;
    
    .icon {
      background: ${props => props.theme.colors.secondary};
      color: white;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.2rem;
      flex-shrink: 0;
    }
    
    .content {
      h4 {
        color: ${props => props.theme.colors.primary};
        margin-bottom: 8px;
        font-size: 1.1rem;
      }
      
      p, a {
        color: #000000;
        line-height: 1.6;
        text-decoration: none;
        font-family: 'Times New Roman', Times, serif;
        
        &:hover {
          color: ${props => props.theme.colors.secondary};
        }
      }
    }
  }
`;

const MapSection = styled.section`
  background: ${props => props.theme.colors.light};
  padding: 80px 0;
`;

const MapContainer = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0,0,0,0.1);
  
  .maps-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      grid-template-columns: 1fr;
      gap: 15px;
    }
  }
  
  .map-item {
    height: 450px;
    
    iframe {
      width: 100%;
      height: 100%;
      border: 0;
    }
  }
`;

const EnquiryForm = styled.div`
  background: ${props => props.theme.colors.white};
  padding: 25px;
  border-radius: 0;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  margin: 30px auto 0;
  max-width: 600px;
  width: 100%;
  border: 1px solid #e0e0e0;
  
  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 20px;
    text-align: center;
    font-size: 1.3rem;
    font-weight: 600;
  }
  
  form {
    display: grid;
    gap: 15px;
  }
`;

const FormField = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`;

const EnquiryInput = styled.input`
  width: 100%;
  padding: 15px;
  border: 1px solid #dee2e6;
  border-radius: 0;
  font-size: 1rem;
  font-family: 'Times New Roman', Times, serif;
  font-weight: 500;
  outline: none;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: #1a4d80;
  }
`;

const EnquiryTextArea = styled.textarea`
  width: 100%;
  padding: 15px;
  border: 1px solid #dee2e6;
  border-radius: 0;
  font-size: 1rem;
  font-family: 'Times New Roman', Times, serif;
  font-weight: 500;
  outline: none;
  transition: border-color 0.3s ease;
  resize: vertical;
  min-height: 120px;
  
  &:focus {
    border-color: #1a4d80;
  }
`;

const EnquirySubmitButton = styled.button`
  padding: 15px 30px;
  background-color: #1a4d80;
  color: white;
  border: none;
  border-radius: 0;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background-color: #2c5aa0;
    transform: translateY(-2px);
  }
`;

const EnquirySuccessMessage = styled.p`
  color: red;
  text-align: center;
  margin: 10px 0 0 0;
  font-size: 0.9rem;
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [enquiryData, setEnquiryData] = useState({
    user_name: '',
    user_email: '',
    user_number: '',
    user_subject: '',
    user_message: ''
  });

  const [hoursDropdownOpen, setHoursDropdownOpen] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
  };

  const handleEnquiryInputChange = (e) => {
    const { name, value } = e.target;
    setEnquiryData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEnquirySubmit = (e) => {
    e.preventDefault();
    // Handle enquiry form submission here
    console.log('Enquiry form submitted:', enquiryData);
    alert('Thank you for your enquiry! We will get back to you soon.');
    setEnquiryData({
      user_name: '',
      user_email: '',
      user_number: '',
      user_subject: '',
      user_message: ''
    });
  };

  const toggleHoursDropdown = () => {
    setHoursDropdownOpen(!hoursDropdownOpen);
  };

  return (
    <ContactContainer>
      <SEO
        title="Contact Tent House in Bangalore for Event Rentals | Trimurthy Tent House"
        description="Need tent or event rental services in Bangalore? Contact Trimurthy Tent House for pricing, availability and bookings. Call +91 88808 28698."
        canonical="https://ttheventorganisers.in/contact"
      />
      <HeroSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 style={{ color: '#ffffff' }}>Contact Us</h1>
            <p style={{ 
              fontSize: '1.5rem', 
              opacity: 0.9, 
              maxWidth: '600px', 
              margin: '20px auto 0',
              color: '#ffffff',
              fontWeight: 600,
              textShadow: '1px 1px 6px rgba(0,0,0,0.5)',
              fontFamily: "'Times New Roman', Times, serif"
            }}>
              Let's Create Your Perfect Event Together - Reach Out to Our Expert Team
            </p>
          </motion.div>
        </Container>
      </HeroSection>

      <Section>
        <Container>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Get In Touch
          </SectionTitle>
          
          <ContactGrid>
            <ContactForm
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              onSubmit={handleSubmit}
            >
              <h3>Send us a Message</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="service">Service Required</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                >
                  <option value="">Select a service</option>
                  <option value="german-hangers">German Hangers</option>
                  <option value="Barricading">Barricading</option>
                  <option value="Carpets">Carpets</option>
                  <option value="Backdrops">Backdrops</option>
                  <option value="Arabian Tents">Arabian Tents</option>
                  <option value="Table Rental">Table Rental</option>
                  <option value="Furniture Rental">Furniture Rental</option>
                  <option value="Shamiyana Rental">Furniture Rental</option>
                  <option value="Stages & Platforms">Stages & Platforms</option>
                  <option value="China Tents">China Tents</option>
                  <option value="Vessels Banquet Chairs">Vessels Banquet Chairs</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your event requirements..."
                  required
                />
              </div>
              
              <button type="submit" className="submit-btn">
                <FiSend />
                Send Message
              </button>
            </ContactForm>

            <ContactInfo
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3>Contact Information</h3>
              
              <div className="contact-item">
                <div className="icon">
                  <FiPhone />
                </div>
                <div className="content">
                  <h4>Phone Number</h4>
                  <p>
                    <a href="tel:+918880828698">+91 88808 28698</a>
                  </p>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="icon">
                  <FiMail />
                </div>
                <div className="content">
                  <h4>Email Address</h4>
                  <p>
                    <a href="mailto:suryakiran1321@gmail.com">suryakiran1321@gmail.com</a>
                  </p>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="icon">
                  <FiMapPin />
                </div>
                <div className="content">
                  <h4>Corporate Address</h4>
                  <p>
                    80feet, Omkar Hills Rd,<br />
                    Sri Krishna Garden Layout,<br />
                    Rajarajeshwari Nagar,<br />
                    Bengaluru, Karnataka 560059
                  </p>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="icon">
                  <FiClock />
                </div>
                <div className="content">
                  <h4>Business Hours</h4>
                  <div style={{ 
                    background: '#f8f9fa', 
                    borderRadius: '8px', 
                    border: '1px solid #e9ecef',
                    overflow: 'hidden'
                  }}>
                    {/* Dropdown Header */}
                    <div 
                      onClick={toggleHoursDropdown}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        padding: '15px',
                        cursor: 'pointer',
                        background: '#f8f9fa',
                        borderBottom: hoursDropdownOpen ? '1px solid #e9ecef' : 'none',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    >
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '16px', color: '#198637' }}>🕒</span>
                        <span style={{ fontWeight: '500', color: '#198637' }}>Open 24 hours</span>
                      </div>
                      <span style={{ 
                        fontSize: '18px', 
                        color: '#666',
                        transform: hoursDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                      }}>
                        ▼
                      </span>
                    </div>
                    
                    {/* Dropdown Content */}
                    {hoursDropdownOpen && (
                      <div style={{ 
                        background: 'white',
                        padding: '15px'
                      }}>
                        <table style={{ 
                          width: '100%', 
                          borderCollapse: 'collapse',
                          fontSize: '14px'
                        }}>
                          <tbody>
                            <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                              <td style={{ padding: '8px 0', fontWeight: '500', width: '30%' }}>Monday</td>
                              <td style={{ padding: '8px 0', color: '#198637' }}>Open 24 hours</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                              <td style={{ padding: '8px 0', fontWeight: '500', width: '30%' }}>Tuesday</td>
                              <td style={{ padding: '8px 0', color: '#198637' }}>Open 24 hours</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                              <td style={{ padding: '8px 0', fontWeight: '500', width: '30%' }}>Wednesday</td>
                              <td style={{ padding: '8px 0', color: '#198637' }}>Open 24 hours</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                              <td style={{ padding: '8px 0', fontWeight: '500', width: '30%' }}>Thursday</td>
                              <td style={{ padding: '8px 0', color: '#198637' }}>Open 24 hours</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                              <td style={{ padding: '8px 0', fontWeight: '500', width: '30%' }}>Friday</td>
                              <td style={{ padding: '8px 0', color: '#198637' }}>Open 24 hours</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                              <td style={{ padding: '8px 0', fontWeight: '500', width: '30%' }}>Saturday</td>
                              <td style={{ padding: '8px 0', color: '#198637' }}>Open 24 hours</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '8px 0', fontWeight: '500', width: '30%' }}>Sunday</td>
                              <td style={{ padding: '8px 0', color: '#198637' }}>Open 24 hours</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </ContactInfo>
          </ContactGrid>
          
          {/* Additional Map Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{ marginTop: '60px' }}
          >
            <h3 style={{ 
              textAlign: 'center', 
              marginBottom: '30px', 
              color: '#1a4d80',
              fontSize: '1.5rem'
            }}>
              Visit Our Location
            </h3>
            <div style={{ 
              background: 'white',
              borderRadius: '15px',
              overflow: 'hidden',
              boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
              maxWidth: '800px',
              margin: '0 auto'
            }}>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.419239416679!2d77.5064523!3d12.9124402!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3f67ce67a8b9%3A0x7e57278a3c3827b7!2sTrimurthy%20Tent%20House%20%7C%20Surya%20Flower%20Decoration%20%7C%20Catering%20%7C%20Event%20Planner!5e0!3m2!1sen!2sin!4v1691234567890!5m1!1e1"
                title="Trimurthy Tent House | Surya Flower Decoration | Catering | Event Planner"
                style={{ width: '100%', height: '400px', border: '0' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </motion.div>
        </Container>
      </Section>

      <Section>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <EnquiryForm>
              <h3 style={{
                color: '#1a4d80',
                textAlign: 'center',
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '30px',
                marginTop: '0'
              }}>Enquiry Form</h3>
              <form action="" method="post" id="first_form" name="enquiryform" onSubmit={handleEnquirySubmit}>
                <FormField>
                  <EnquiryInput 
                    placeholder="Your name" 
                    type="text" 
                    name="user_name" 
                    value={enquiryData.user_name}
                    onChange={handleEnquiryInputChange}
                    tabIndex="1"
                  />
                </FormField>
                <FormField>
                  <EnquiryInput 
                    placeholder="Your Email Address" 
                    name="user_email" 
                    type="email" 
                    value={enquiryData.user_email}
                    onChange={handleEnquiryInputChange}
                    tabIndex="2"
                  />
                </FormField>
                <FormField>
                  <EnquiryInput 
                    placeholder="Your Phone Number" 
                    name="user_number" 
                    type="tel" 
                    value={enquiryData.user_number}
                    onChange={handleEnquiryInputChange}
                    tabIndex="3"
                  />
                </FormField>

                <FormField>
                  <EnquiryTextArea 
                    placeholder="Type your message here...." 
                    name="user_message" 
                    value={enquiryData.user_message}
                    onChange={handleEnquiryInputChange}
                    tabIndex="4"
                  />
                </FormField>
                <FormField>
                  <EnquirySubmitButton 
                    name="submit" 
                    type="submit" 
                    id="submit" 
                    data-submit="...Sending"
                  >
                    Submit
                  </EnquirySubmitButton>
                </FormField>
                <EnquirySuccessMessage className="copyright" id="success" />
              </form>
            </EnquiryForm>
          </motion.div>
        </Container>
      </Section>
    </ContactContainer>
  );
};

export default Contact; 